import { Avatar, AvatarFallback } from '@/components/ui/shadcn/avatar';
import { Badge } from '@/components/ui/shadcn/badge';
import { Button } from '@/components/ui/shadcn/button';
import { Card } from '@/components/ui/shadcn/card';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/shadcn/dropdown-menu';
import { Input } from '@/components/ui/shadcn/input';
import { Progress } from '@/components/ui/shadcn/progress';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/shadcn/table';
import { useTranslations } from '@/hooks/useTranslations';
import { laravelJsonFetch } from '@/lib/laravelJsonFetch';
import { cn } from '@/lib/utils';
import type { Project } from '@/types';
import axios, { AxiosProgressEvent } from 'axios';
import {
    Cloud,
    CloudUpload,
    ExternalLink,
    FileArchive,
    FileAudio,
    FileImage,
    FileSpreadsheet,
    FileText,
    FileVideo,
    Folder,
    FolderPlus,
    Loader2,
    MoreHorizontal,
    Plus,
    RefreshCw,
    Search,
    Trash2,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

interface BrowseFolder {
    id: string;
    name: string;
    web_url: string;
    child_count: number;
    modified: string | null;
}

interface BrowseFile {
    id?: string; // project_files uuid when indexed
    sharepoint_item_id: string;
    name: string;
    mime: string | null;
    size: number;
    web_url: string;
    created_at: string | null;
    sharepoint_modified_at: string | null;
    uploaded_by: { id: string; name: string } | null;
}

interface BrowsePayload {
    provisioned: boolean;
    provision_status?: string | null;
    is_root?: boolean;
    folder_id?: string;
    root_id?: string;
    web_url?: string | null;
    folders?: BrowseFolder[];
    files?: BrowseFile[];
}

interface UploadCard {
    id: string;
    filename: string;
    size: number;
    progress: number;
    status: 'uploading' | 'queued' | 'failed' | 'completed';
    error?: string;
}

interface Crumb {
    id: string; // '' = project root
    name: string;
}

interface Props {
    project: Project;
}

function formatBytes(n: number | null): string {
    if (!n) return '—';
    const units = ['B', 'KB', 'MB', 'GB'];
    let v = n;
    let u = 0;
    while (v >= 1024 && u < units.length - 1) {
        v /= 1024;
        u += 1;
    }
    return `${v.toFixed(v >= 100 ? 0 : 1)} ${units[u]}`;
}

function getFileExtension(name: string): string {
    const dot = name.lastIndexOf('.');
    if (dot === -1) return '';
    return name.slice(dot + 1).toUpperCase();
}

function getFileIcon(mime: string | null, name: string) {
    const ext = getFileExtension(name).toLowerCase();
    const m = (mime ?? '').toLowerCase();
    if (m.startsWith('image/') || ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp'].includes(ext)) return FileImage;
    if (m.startsWith('video/') || ['mp4', 'mov', 'avi', 'webm', 'mkv'].includes(ext)) return FileVideo;
    if (m.startsWith('audio/') || ['mp3', 'wav', 'ogg', 'flac', 'm4a'].includes(ext)) return FileAudio;
    if (m.includes('spreadsheet') || m.includes('excel') || ['xlsx', 'xls', 'csv', 'numbers'].includes(ext)) return FileSpreadsheet;
    if (m.includes('zip') || m.includes('compressed') || ['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) return FileArchive;
    return FileText;
}

function getTypeBadgeClasses(ext: string): string {
    const e = ext.toLowerCase();
    if (['pdf'].includes(e)) return 'bg-danger-subtle text-danger-subtle-foreground border-danger-subtle-foreground/30';
    if (['xlsx', 'xls', 'csv', 'numbers'].includes(e)) return 'bg-success-subtle text-success-subtle-foreground border-success-subtle-foreground/30';
    if (['dwg', 'dxf'].includes(e)) return 'bg-warning-subtle text-warning-subtle-foreground border-warning-subtle-foreground/30';
    if (['doc', 'docx', 'rtf', 'odt'].includes(e)) return 'bg-cat-indigo text-cat-indigo-foreground border-cat-indigo-foreground/30';
    if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp'].includes(e)) return 'bg-cat-cyan text-cat-cyan-foreground border-cat-cyan-foreground/30';
    if (['mp4', 'mov', 'avi', 'webm', 'mkv'].includes(e)) return 'bg-cat-purple text-cat-purple-foreground border-cat-purple-foreground/30';
    if (['zip', 'rar', '7z', 'tar', 'gz'].includes(e)) return 'bg-warning-subtle text-warning-subtle-foreground border-warning-subtle-foreground/30';
    return 'bg-muted text-muted-foreground border-border';
}

function getAvatarColor(seed: string): string {
    const colors = [
        'bg-success text-white',
        'bg-cat-purple-foreground text-white',
        'bg-cat-indigo-foreground text-white',
        'bg-warning text-white',
        'bg-cat-pink-foreground text-white',
        'bg-cat-cyan-foreground text-white',
        'bg-info text-white',
        'bg-cat-teal-foreground text-white',
    ];
    let hash = 0;
    for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
    return colors[hash % colors.length];
}

function getInitials(name: string): string {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export default function ProjectFiles({ project }: Props) {
    const { t } = useTranslations();
    const projectId = project.id;

    const [payload, setPayload] = useState<BrowsePayload | null>(null);
    const [loading, setLoading] = useState(true);
    const [provisioning, setProvisioning] = useState(false);
    const [creatingStd, setCreatingStd] = useState(false);
    const [search, setSearch] = useState('');
    const [uploads, setUploads] = useState<UploadCard[]>([]);
    const [dragging, setDragging] = useState(false);
    const [dragOverFolder, setDragOverFolder] = useState<string | null>(null);
    const [crumbs, setCrumbs] = useState<Crumb[]>([{ id: '', name: t('projects.show.files.root_label', 'Files') }]);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const autoProvisionedRef = useRef(false);

    const currentFolderId = crumbs[crumbs.length - 1]?.id ?? '';
    const isRoot = currentFolderId === '';

    const browse = useCallback(
        async (folderId: string, silent = false) => {
            if (!silent) setLoading(true);
            try {
                const qs = folderId ? `?folder=${encodeURIComponent(folderId)}` : '';
                const res = await fetch(`/projects/${projectId}/files/browse${qs}`, {
                    headers: { Accept: 'application/json' },
                    credentials: 'same-origin',
                });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const data = (await res.json()) as BrowsePayload;
                setPayload(data);
            } catch (err) {
                if (!silent) toast.error(t('projects.show.files.browse_failed', 'Failed to load folder contents.'));

                console.error('ProjectFiles: browse failed', err);
            } finally {
                if (!silent) setLoading(false);
            }
        },
        [projectId, t],
    );

    // Load current folder whenever the breadcrumb target changes.
    useEffect(() => {
        void browse(currentFolderId);
    }, [browse, currentFolderId]);

    const handleProvision = useCallback(async () => {
        setProvisioning(true);
        try {
            await laravelJsonFetch(`/projects/${projectId}/files/provision`, 'POST');
            toast.success(t('projects.show.files.provision_queued', 'Folder provisioning started. This may take a few seconds.'));
            for (let i = 0; i < 8; i++) {
                await new Promise((r) => setTimeout(r, 2000));
                await browse('', true);
                // Stop early once provisioned.
            }
        } catch (err) {
            toast.error(t('projects.show.files.provision_failed', 'Failed to provision folder: {error}').replace('{error}', (err as Error).message));
        } finally {
            setProvisioning(false);
            void browse('', true);
        }
    }, [projectId, browse, t]);

    // Auto-provision on first open if never attempted.
    useEffect(() => {
        if (
            !autoProvisionedRef.current &&
            payload !== null &&
            !loading &&
            !provisioning &&
            payload.provisioned === false &&
            (payload.provision_status ?? null) === null
        ) {
            autoProvisionedRef.current = true;
            void handleProvision();
        }
    }, [payload, loading, provisioning, handleProvision]);

    // Echo: refresh current folder on upload events.
    useEffect(() => {
        if (typeof window === 'undefined' || !window.Echo) return;
        const channel = window.Echo.private(`project.${projectId}.files`);
        channel.listen('ProjectFileUploaded', () => void browse(currentFolderId, true));
        channel.listen('ProjectFileUploadProgress', (e: { upload_id: string; percent: number }) => {
            setUploads((cur) => cur.map((u) => (u.id === e.upload_id ? { ...u, progress: e.percent, status: 'uploading' } : u)));
        });
        channel.listen('ProjectFileUploadFailed', (e: { upload_id: string; error: string }) => {
            setUploads((cur) => cur.map((u) => (u.id === e.upload_id ? { ...u, status: 'failed', error: e.error } : u)));
            toast.error(t('projects.show.files.upload_failed', 'Upload failed: {error}').replace('{error}', e.error));
        });
        channel.listen('ProjectFileArchived', () => void browse(currentFolderId, true));
        return () => {
            channel.stopListening('ProjectFileUploaded');
            channel.stopListening('ProjectFileUploadProgress');
            channel.stopListening('ProjectFileUploadFailed');
            channel.stopListening('ProjectFileArchived');
            window.Echo.leaveChannel(`private-project.${projectId}.files`);
        };
    }, [projectId, currentFolderId, browse, t]);

    const handleFiles = useCallback(
        async (files: FileList | File[]) => {
            const arr = Array.from(files);
            if (arr.length === 0 || !payload?.provisioned) return;

            for (const file of arr) {
                const tempId = `temp-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`;
                setUploads((cur) => [...cur, { id: tempId, filename: file.name, size: file.size, progress: 0, status: 'uploading' }]);

                const form = new FormData();
                form.append('file', file);
                if (currentFolderId) form.append('parent_item_id', currentFolderId);

                try {
                    const res = await axios.post<{ upload_id: string }>(`/projects/${projectId}/files`, form, {
                        headers: { 'X-Requested-With': 'XMLHttpRequest' },
                        onUploadProgress: (e: AxiosProgressEvent) => {
                            if (!e.total) return;
                            const pct = Math.min(99, Math.round((e.loaded / e.total) * 100));
                            setUploads((cur) => cur.map((u) => (u.id === tempId ? { ...u, progress: pct } : u)));
                        },
                    });
                    setUploads((cur) => cur.map((u) => (u.id === tempId ? { ...u, id: res.data.upload_id, status: 'queued' } : u)));
                } catch (err) {
                    setUploads((cur) => cur.map((u) => (u.id === tempId ? { ...u, status: 'failed', error: (err as Error).message } : u)));
                    toast.error(t('projects.show.files.upload_failed', 'Upload failed: {error}').replace('{error}', (err as Error).message));
                }
            }
        },
        [projectId, payload, currentFolderId, t],
    );

    // Mark uploads complete when the file appears in the current listing.
    useEffect(() => {
        if (!payload?.files) return;
        setUploads((cur) =>
            cur.map((u) =>
                u.status === 'queued' || u.status === 'uploading'
                    ? payload.files!.some((f) => f.name === u.filename)
                        ? { ...u, status: 'completed', progress: 100 }
                        : u
                    : u,
            ),
        );
    }, [payload]);

    // Drain finished upload cards.
    useEffect(() => {
        const timers: number[] = [];
        uploads.forEach((u) => {
            if (u.status === 'completed' || u.status === 'failed') {
                timers.push(window.setTimeout(() => setUploads((cur) => cur.filter((c) => c.id !== u.id)), 8000));
            }
        });
        return () => timers.forEach((id) => window.clearTimeout(id));
    }, [uploads]);

    const handleNewFolder = useCallback(async () => {
        const name = window.prompt(t('projects.show.files.new_folder_prompt', 'New folder name'));
        if (!name || !name.trim()) return;
        try {
            await laravelJsonFetch(`/projects/${projectId}/files/folder`, 'POST', {
                name: name.trim(),
                parent_item_id: currentFolderId || undefined,
            });
            toast.success(t('projects.show.files.folder_created', 'Folder created.'));
            void browse(currentFolderId, true);
        } catch (err) {
            toast.error(t('projects.show.files.folder_create_failed', 'Failed to create folder: {error}').replace('{error}', (err as Error).message));
        }
    }, [projectId, currentFolderId, browse, t]);

    const handleCreateStandard = useCallback(async () => {
        setCreatingStd(true);
        try {
            const res = await laravelJsonFetch<{ created: number; existing: number }>(`/projects/${projectId}/files/standard-folders`, 'POST');
            toast.success(
                t('projects.show.files.standard_folders_created', 'Created {created} folder(s), {existing} already existed.')
                    .replace('{created}', String(res.created))
                    .replace('{existing}', String(res.existing)),
            );
            void browse(currentFolderId, true);
        } catch (err) {
            toast.error(t('projects.show.files.folder_create_failed', 'Failed to create folder: {error}').replace('{error}', (err as Error).message));
        } finally {
            setCreatingStd(false);
        }
    }, [projectId, currentFolderId, browse, t]);

    const handleArchive = useCallback(
        async (file: BrowseFile) => {
            if (!window.confirm(t('projects.show.files.delete_confirm', 'Move "{name}" to the Archive folder?').replace('{name}', file.name))) {
                return;
            }
            try {
                await laravelJsonFetch(`/projects/${projectId}/files/archive-item`, 'POST', {
                    item_id: file.sharepoint_item_id,
                });
                toast.success(t('projects.show.files.archive_queued', 'Archive queued.'));
                void browse(currentFolderId, true);
            } catch (err) {
                toast.error(t('projects.show.files.archive_failed', 'Failed to archive: {error}').replace('{error}', (err as Error).message));
            }
        },
        [projectId, currentFolderId, browse, t],
    );

    const handleMove = useCallback(
        async (itemId: string, itemName: string, targetId: string, targetName: string) => {
            try {
                await laravelJsonFetch(`/projects/${projectId}/files/move-item`, 'POST', {
                    item_id: itemId,
                    target_parent_item_id: targetId,
                });
                toast.success(
                    t('projects.show.files.moved', 'Moved "{name}" to "{folder}".').replace('{name}', itemName).replace('{folder}', targetName),
                );
                void browse(currentFolderId, true);
            } catch (err) {
                toast.error(t('projects.show.files.move_failed', 'Failed to move: {error}').replace('{error}', (err as Error).message));
            }
        },
        [projectId, currentFolderId, browse, t],
    );

    // Read a dragged internal item from a drop event, if present.
    const readDraggedItem = (e: React.DragEvent): { id: string; name: string } | null => {
        const raw = e.dataTransfer.getData('application/x-crm-item');
        if (!raw) return null;
        try {
            return JSON.parse(raw) as { id: string; name: string };
        } catch {
            return null;
        }
    };

    const startItemDrag = (e: React.DragEvent, id: string, name: string) => {
        e.dataTransfer.setData('application/x-crm-item', JSON.stringify({ id, name }));
        e.dataTransfer.effectAllowed = 'move';
    };

    const navigateInto = (folder: BrowseFolder) => setCrumbs((c) => [...c, { id: folder.id, name: folder.name }]);
    const navigateTo = (index: number) => setCrumbs((c) => c.slice(0, index + 1));

    const q = search.trim().toLowerCase();
    const folders = useMemo(() => {
        const list = (payload?.folders ?? []).filter((f) => (q ? f.name.toLowerCase().includes(q) : true));
        // Sort by the leading numeric prefix ("1.", "2." … "11.") so 11 comes
        // after 2 instead of lexically before it. Unnumbered folders sort last.
        const leadingNum = (name: string): number => {
            const m = name.match(/^\s*(\d+)/);
            return m ? parseInt(m[1], 10) : Number.MAX_SAFE_INTEGER;
        };
        return [...list].sort((a, b) => {
            const na = leadingNum(a.name);
            const nb = leadingNum(b.name);
            if (na !== nb) return na - nb;
            return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' });
        });
    }, [payload, q]);
    const files = useMemo(
        () =>
            (payload?.files ?? []).filter((f) =>
                q ? f.name.toLowerCase().includes(q) || (f.uploaded_by?.name ?? '').toLowerCase().includes(q) : true,
            ),
        [payload, q],
    );

    const provisioned = payload?.provisioned === true;
    const status = payload?.provision_status ?? null;
    const inFlight = !provisioned && (provisioning || status === 'pending' || status === null);
    const failed = !provisioned && status === 'failed' && !provisioning;

    return (
        <section className="space-y-5">
            {/* Header — the section sits inside the details layout, so it carries no title of its own. */}
            <header className="flex flex-wrap items-center justify-end gap-3">
                {payload?.web_url && (
                    <Button asChild type="button" variant="outline" size="sm">
                        <a href={payload.web_url} target="_blank" rel="noreferrer" className="gap-2">
                            <Cloud className="h-3.5 w-3.5" />
                            {t('projects.show.files.open_in_sharepoint', 'Open in SharePoint')}
                        </a>
                    </Button>
                )}
            </header>

            {/* Folder failed banner */}
            {failed && (
                <Card className="border-warning bg-warning-subtle p-4">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="flex items-start gap-3">
                            <FolderPlus className="text-warning-subtle-foreground mt-0.5 h-5 w-5 shrink-0" />
                            <div className="space-y-1">
                                <div className="text-warning-subtle-foreground text-sm font-medium">
                                    {t('projects.show.files.folder_failed_title', 'SharePoint folder failed to provision')}
                                </div>
                                <p className="text-warning-subtle-foreground/80 text-xs">
                                    {t('projects.show.files.no_folder_description', 'Click below to create the project folder in SharePoint.')}
                                </p>
                            </div>
                        </div>
                        <Button type="button" size="sm" onClick={() => void handleProvision()} disabled={provisioning} className="gap-2">
                            {provisioning ? <Loader2 className="h-3 w-3 animate-spin" /> : <FolderPlus className="h-3 w-3" />}
                            {t('projects.show.files.create_folder', 'Create folder')}
                        </Button>
                    </div>
                </Card>
            )}

            {/* In-flight provisioning */}
            {inFlight && (
                <Card className="border-2 border-dashed py-10">
                    <div className="text-muted-foreground flex flex-col items-center gap-3 text-sm">
                        <Loader2 className="h-8 w-8 animate-spin opacity-50" />
                        {t('projects.show.files.folder_pending', 'Creating SharePoint folder…')}
                    </div>
                </Card>
            )}

            {provisioned && (
                <Card className="overflow-hidden">
                    {/* Toolbar: breadcrumbs + actions */}
                    <div className="flex flex-wrap items-center gap-3 border-b p-3 px-4">
                        {/* Breadcrumbs */}
                        <nav className="flex min-w-0 flex-1 items-center gap-1 text-sm">
                            {crumbs.map((c, i) => {
                                const isCurrent = i === crumbs.length - 1;
                                const dropTargetId = c.id || (payload?.root_id ?? '');
                                const crumbKey = `crumb-${c.id || 'root'}`;
                                return (
                                    <span key={crumbKey} className="flex min-w-0 items-center gap-1">
                                        {i > 0 && <span className="text-muted-foreground">/</span>}
                                        <button
                                            type="button"
                                            onClick={() => navigateTo(i)}
                                            disabled={isCurrent}
                                            onDragOver={(e) => {
                                                if (isCurrent || !dropTargetId || !e.dataTransfer.types.includes('application/x-crm-item')) return;
                                                e.preventDefault();
                                                e.dataTransfer.dropEffect = 'move';
                                                if (dragOverFolder !== crumbKey) setDragOverFolder(crumbKey);
                                            }}
                                            onDragLeave={() => setDragOverFolder((cur) => (cur === crumbKey ? null : cur))}
                                            onDrop={(e) => {
                                                if (isCurrent || !dropTargetId) return;
                                                const dragged = readDraggedItem(e);
                                                if (!dragged) return;
                                                e.preventDefault();
                                                setDragOverFolder(null);
                                                if (dragged.id !== dropTargetId) {
                                                    void handleMove(dragged.id, dragged.name, dropTargetId, c.name);
                                                }
                                            }}
                                            className={cn(
                                                'truncate rounded px-1.5 py-0.5',
                                                isCurrent
                                                    ? 'text-foreground font-medium'
                                                    : 'text-muted-foreground hover:bg-accent hover:text-foreground',
                                                dragOverFolder === crumbKey && 'bg-primary/10 ring-primary/40 ring-1',
                                            )}
                                        >
                                            {c.name}
                                        </button>
                                    </span>
                                );
                            })}
                        </nav>

                        <div className="flex flex-wrap items-center gap-2">
                            <div className="relative">
                                <Search className="text-muted-foreground absolute top-1/2 left-2.5 h-3.5 w-3.5 -translate-y-1/2" />
                                <Input
                                    type="search"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder={t('projects.show.files.search_placeholder', 'Search files')}
                                    className="h-9 w-40 pl-8"
                                />
                            </div>
                            <Button type="button" size="sm" onClick={() => fileInputRef.current?.click()} className="gap-2">
                                <CloudUpload className="h-3.5 w-3.5" />
                                {t('projects.show.files.upload_here', 'Upload here')}
                            </Button>
                            <Button type="button" variant="outline" size="sm" onClick={() => void handleNewFolder()} className="gap-2">
                                <FolderPlus className="h-3.5 w-3.5" />
                                {t('projects.show.files.new_folder', 'New folder')}
                            </Button>
                            {isRoot && (
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => void handleCreateStandard()}
                                    disabled={creatingStd}
                                    className="gap-2"
                                >
                                    {creatingStd ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                                    {t('projects.show.files.create_standard_folders', 'Create standard folders')}
                                </Button>
                            )}
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-9 w-9"
                                onClick={() => void browse(currentFolderId)}
                                disabled={loading}
                            >
                                <RefreshCw className={cn('h-3.5 w-3.5', loading && 'animate-spin')} />
                            </Button>
                            <input
                                ref={fileInputRef}
                                type="file"
                                multiple
                                className="hidden"
                                onChange={(e) => {
                                    if (e.target.files) void handleFiles(e.target.files);
                                    e.target.value = '';
                                }}
                            />
                        </div>
                    </div>

                    {/* Drop area / content */}
                    <div
                        onDragOver={(e) => {
                            // Only show the upload affordance for external file drags.
                            if (e.dataTransfer.types.includes('application/x-crm-item')) return;
                            e.preventDefault();
                            setDragging(true);
                        }}
                        onDragLeave={() => setDragging(false)}
                        onDrop={(e) => {
                            // Ignore internal item moves dropped on empty space.
                            if (e.dataTransfer.types.includes('application/x-crm-item')) return;
                            e.preventDefault();
                            setDragging(false);
                            void handleFiles(e.dataTransfer.files);
                        }}
                        className={cn('relative', dragging && 'ring-primary bg-primary/5 ring-2 ring-inset')}
                    >
                        {/* Active uploads */}
                        {uploads.length > 0 && (
                            <div className="space-y-2 border-b p-3">
                                {uploads.map((u) => (
                                    <div key={u.id} className="rounded-md border p-2 text-sm">
                                        <div className="flex items-center justify-between gap-3">
                                            <span className="truncate font-medium">{u.filename}</span>
                                            {u.status === 'completed' && <Badge variant="default">done</Badge>}
                                            {u.status === 'failed' && <Badge variant="destructive">failed</Badge>}
                                            {(u.status === 'queued' || u.status === 'uploading') && (
                                                <Badge variant="secondary" className="gap-1">
                                                    <Loader2 className="h-3 w-3 animate-spin" />
                                                    {u.status === 'queued' ? t('projects.show.files.status.queued', 'queued') : `${u.progress}%`}
                                                </Badge>
                                            )}
                                        </div>
                                        {(u.status === 'queued' || u.status === 'uploading') && <Progress value={u.progress} className="mt-2 h-1" />}
                                    </div>
                                ))}
                            </div>
                        )}

                        {loading ? (
                            <div className="text-muted-foreground py-16 text-center text-sm">
                                <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                                <div className="mt-2">{t('projects.show.files.loading', 'Loading files…')}</div>
                            </div>
                        ) : folders.length === 0 && files.length === 0 ? (
                            <div className="text-muted-foreground py-16 text-center text-sm">
                                <CloudUpload className="mx-auto h-8 w-8 opacity-40" />
                                <div className="mt-2">{t('projects.show.files.empty_folder', 'This folder is empty.')}</div>
                                <div className="mt-1 text-xs">
                                    {t('projects.show.files.dropzone_subtitle', 'Drop files here or pick from your device.')}
                                </div>
                            </div>
                        ) : (
                            <Table>
                                <TableHeader>
                                    <TableRow className="bg-muted/30 hover:bg-muted/30">
                                        <TableHead className="font-medium">{t('projects.show.files.columns.name', 'Name')}</TableHead>
                                        <TableHead className="font-medium">{t('projects.show.files.columns.type', 'Type')}</TableHead>
                                        <TableHead className="font-medium">{t('projects.show.files.columns.size', 'Size')}</TableHead>
                                        <TableHead className="font-medium">{t('projects.show.files.columns.uploaded_by', 'Uploaded by')}</TableHead>
                                        <TableHead className="font-medium">{t('projects.show.files.columns.uploaded_at', 'Uploaded')}</TableHead>
                                        <TableHead className="text-right font-medium">
                                            {t('projects.show.files.columns.actions', 'Actions')}
                                        </TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {/* Folders first */}
                                    {folders.map((folder) => (
                                        <TableRow
                                            key={folder.id}
                                            draggable
                                            onDragStart={(e) => {
                                                e.stopPropagation();
                                                startItemDrag(e, folder.id, folder.name);
                                            }}
                                            onDragOver={(e) => {
                                                if (!e.dataTransfer.types.includes('application/x-crm-item')) return;
                                                e.preventDefault();
                                                e.dataTransfer.dropEffect = 'move';
                                                if (dragOverFolder !== folder.id) setDragOverFolder(folder.id);
                                            }}
                                            onDragLeave={() => setDragOverFolder((cur) => (cur === folder.id ? null : cur))}
                                            onDrop={(e) => {
                                                const dragged = readDraggedItem(e);
                                                if (!dragged) return;
                                                e.preventDefault();
                                                e.stopPropagation();
                                                setDragOverFolder(null);
                                                if (dragged.id !== folder.id) {
                                                    void handleMove(dragged.id, dragged.name, folder.id, folder.name);
                                                }
                                            }}
                                            className={cn(
                                                'hover:bg-accent/50 cursor-pointer',
                                                dragOverFolder === folder.id && 'bg-primary/10 ring-primary/40 ring-1 ring-inset',
                                            )}
                                            onClick={() => navigateInto(folder)}
                                        >
                                            <TableCell>
                                                <div className="flex items-center gap-3">
                                                    <div className="bg-warning-subtle text-warning-subtle-foreground flex h-9 w-9 shrink-0 items-center justify-center rounded-md">
                                                        <Folder className="h-4 w-4" />
                                                    </div>
                                                    <span className="text-sm font-medium">{folder.name}</span>
                                                    {dragOverFolder === folder.id && (
                                                        <span className="text-primary text-xs">
                                                            {t('projects.show.files.drop_to_move', 'Drop to move here')}
                                                        </span>
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell />
                                            <TableCell className="text-muted-foreground text-xs">
                                                {t('projects.show.files.item_count', '{count} item(s)').replace(
                                                    '{count}',
                                                    String(folder.child_count),
                                                )}
                                            </TableCell>
                                            <TableCell />
                                            <TableCell className="text-muted-foreground text-sm whitespace-nowrap">
                                                {folder.modified ? new Date(folder.modified).toLocaleDateString() : '—'}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-7 text-xs"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigateInto(folder);
                                                    }}
                                                >
                                                    {t('projects.show.files.open_folder', 'Open folder')}
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}

                                    {/* Files */}
                                    {files.map((file) => {
                                        const Icon = getFileIcon(file.mime, file.name);
                                        const ext = getFileExtension(file.name);
                                        const uploaderName = file.uploaded_by?.name?.trim() || '';
                                        return (
                                            <TableRow
                                                key={file.sharepoint_item_id}
                                                draggable
                                                onDragStart={(e) => startItemDrag(e, file.sharepoint_item_id, file.name)}
                                                className="cursor-grab active:cursor-grabbing"
                                            >
                                                <TableCell className="max-w-[360px]">
                                                    <div className="flex items-center gap-3">
                                                        <div className="bg-muted text-muted-foreground flex h-9 w-9 shrink-0 items-center justify-center rounded-md">
                                                            <Icon className="h-4 w-4" />
                                                        </div>
                                                        <a
                                                            href={file.web_url}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            title={file.name}
                                                            className="truncate text-sm font-medium hover:underline"
                                                        >
                                                            {file.name}
                                                        </a>
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    {ext && (
                                                        <Badge variant="outline" className={cn('font-medium', getTypeBadgeClasses(ext))}>
                                                            {ext}
                                                        </Badge>
                                                    )}
                                                </TableCell>
                                                <TableCell className="text-muted-foreground text-sm">{formatBytes(file.size)}</TableCell>
                                                <TableCell>
                                                    {uploaderName ? (
                                                        <div className="flex items-center gap-2">
                                                            <Avatar className="h-7 w-7">
                                                                <AvatarFallback className={cn('text-xs font-medium', getAvatarColor(uploaderName))}>
                                                                    {getInitials(uploaderName)}
                                                                </AvatarFallback>
                                                            </Avatar>
                                                            <span className="text-sm">{uploaderName}</span>
                                                        </div>
                                                    ) : (
                                                        <span className="text-muted-foreground text-xs">—</span>
                                                    )}
                                                </TableCell>
                                                <TableCell className="text-muted-foreground text-sm whitespace-nowrap">
                                                    {file.created_at ? new Date(file.created_at).toLocaleString() : '—'}
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    <DropdownMenu>
                                                        <DropdownMenuTrigger asChild>
                                                            <Button
                                                                type="button"
                                                                variant="ghost"
                                                                size="icon"
                                                                className="h-8 w-8"
                                                                aria-label={t('projects.show.files.action_menu_label', 'File actions')}
                                                            >
                                                                <MoreHorizontal className="h-4 w-4" />
                                                            </Button>
                                                        </DropdownMenuTrigger>
                                                        <DropdownMenuContent align="end" className="min-w-[180px]">
                                                            <DropdownMenuItem asChild>
                                                                <a href={file.web_url} target="_blank" rel="noreferrer" className="gap-2">
                                                                    <ExternalLink className="h-3.5 w-3.5" />
                                                                    {t('projects.show.files.view', 'View')}
                                                                </a>
                                                            </DropdownMenuItem>
                                                            <DropdownMenuSeparator />
                                                            <DropdownMenuItem
                                                                onClick={() => void handleArchive(file)}
                                                                className="text-destructive focus:text-destructive gap-2"
                                                            >
                                                                <Trash2 className="h-3.5 w-3.5" />
                                                                {t('projects.show.files.delete', 'Move to Archive')}
                                                            </DropdownMenuItem>
                                                        </DropdownMenuContent>
                                                    </DropdownMenu>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        )}
                    </div>
                </Card>
            )}
        </section>
    );
}
